import React, {useState, useEffect, useCallback} from "react";
import {Table, Spin} from "antd";
import {CalendarOutlined, UpOutlined, DownOutlined, UserOutlined} from "@ant-design/icons";
import moment from "moment";
import {DefaultTransactionTable} from "../../components/DefaultTransactionTable";
import {
    SubcompanyTableWrapper,
    DaysContainer,
    DayWrapper,
    DayRow,
    DayIcon,
    DayInfo,
    DayTotal,
    ExpandedDayContent,
    DayTransactionTableWrapper,
    CourierBox,
    CourierHeader,
    CourierName,
    CourierTotal,
    CourierContent
} from "../styles/SubcompanyDetailTotal.styles";
import {DETAIL_TABLE_SUB_TYPE_LABELS, normalizeTableData} from "constants/constants";

const formatCurrency = amount => {
    if (amount === undefined || amount === null) return "-";
    if (amount === 0) return "0";
    return `${amount.toLocaleString("tr-TR")} TL`;
};

const CourierSection = ({courier, isExpanded, onToggle}) => {
    const displayName = courier?.courierFullName || "-";

    return (
        <CourierBox>
            <CourierHeader onClick={onToggle}>
                <CourierName>
                    <UserOutlined style={{marginRight: 6}} />
                    {displayName}
                </CourierName>
                <CourierTotal>
                    <span className="amount">{formatCurrency(courier.totalClientAmount)}</span>
                    <span className="arrow">{isExpanded ? <UpOutlined /> : <DownOutlined />}</span>
                </CourierTotal>
            </CourierHeader>
            {isExpanded && (
                <CourierContent>
                    <DefaultTransactionTable
                        transactions={courier.transactions}
                        subTypeLabels={DETAIL_TABLE_SUB_TYPE_LABELS}
                        Wrapper={DayTransactionTableWrapper}
                    />
                </CourierContent>
            )}
        </CourierBox>
    );
};

export const SubcompanyDetailTable = ({
    data,
    loading,
    subCompanyNames,
    pageParam,
    setPageParam,
    sharedLogFilters,
    fetchDetail
}) => {
    const [expandedSubCompanies, setExpandedSubCompanies] = useState([]);
    const [loadingSubCompanyIds, setLoadingSubCompanyIds] = useState([]);
    const [expandedDays, setExpandedDays] = useState({});
    const [expandedCouriers, setExpandedCouriers] = useState({});
    const [detailBySubCompanyId, setDetailBySubCompanyId] = useState({});

    const startDate = sharedLogFilters?.startDate;
    const endDate = sharedLogFilters?.endDate;
    const paymentPeriodId = sharedLogFilters?.paymentPeriodId;
    const hasDateRange = Boolean(startDate && endDate);
    const hasPaymentPeriod = paymentPeriodId != null && paymentPeriodId !== "";

    useEffect(() => {
        setDetailBySubCompanyId({});
        setExpandedSubCompanies([]);
        setLoadingSubCompanyIds([]);
        setExpandedDays({});
        setExpandedCouriers({});
    }, [JSON.stringify(sharedLogFilters)]);

    const loadDetail = useCallback(
        subCompanyId => {
            if (
                !fetchDetail ||
                (!hasDateRange && !hasPaymentPeriod) ||
                detailBySubCompanyId[subCompanyId]
            )
                return;
            setDetailBySubCompanyId(prev => ({
                ...prev,
                [subCompanyId]: {loading: true, data: null, error: null}
            }));
            fetchDetail({
                ...sharedLogFilters,
                groupType: "subCompany",
                subCompanyId
            })
                .then(res => {
                    const detailData = res?.data != null ? res.data : res;
                    setDetailBySubCompanyId(prev => ({
                        ...prev,
                        [subCompanyId]: {
                            loading: false,
                            data: detailData,
                            error: null
                        }
                    }));
                })
                .catch(err => {
                    setDetailBySubCompanyId(prev => ({
                        ...prev,
                        [subCompanyId]: {
                            loading: false,
                            data: null,
                            error: err?.message || "Detay yüklenemedi"
                        }
                    }));
                });
        },
        [
            fetchDetail,
            sharedLogFilters,
            hasDateRange,
            hasPaymentPeriod,
            detailBySubCompanyId
        ]
    );

    const handleSubCompanyExpand = (expanded, record) => {
        const subCompanyId = record?.subcompanyId;
        if (!expanded) {
            setExpandedSubCompanies(prev => prev.filter(id => id !== subCompanyId));
            return;
        }
        const detail = detailBySubCompanyId[subCompanyId];
        if (detail && !detail.loading) {
            setExpandedSubCompanies(prev => [...prev, subCompanyId]);
        } else {
            setLoadingSubCompanyIds(prev => [...prev, subCompanyId]);
            loadDetail(subCompanyId);
        }
    };

    useEffect(() => {
        if (loadingSubCompanyIds.length === 0) return;
        const stillLoading = [];
        const toExpand = [];
        loadingSubCompanyIds.forEach(subCompanyId => {
            const detail = detailBySubCompanyId[subCompanyId];
            if (detail && !detail.loading) {
                toExpand.push(subCompanyId);
            } else {
                stillLoading.push(subCompanyId);
            }
        });
        if (toExpand.length > 0) {
            setLoadingSubCompanyIds(stillLoading);
            setExpandedSubCompanies(prev => [...prev, ...toExpand]);
        }
    }, [detailBySubCompanyId, loadingSubCompanyIds]);

    const toggleDay = (subCompanyId, dayIndex) => {
        const key = `${subCompanyId}-${dayIndex}`;
        setExpandedDays(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const toggleCourier = (subCompanyId, dayIndex, courierId) => {
        const key = `${subCompanyId}-${dayIndex}-${courierId}`;
        setExpandedCouriers(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const renderExpandedSubCompany = subCompany => {
        const subCompanyId = subCompany?.subcompanyId || "-";
        const detail = detailBySubCompanyId[subCompanyId];
        if (detail?.error) {
            return <div style={{padding: 24, color: "#e74c3c"}}>{detail.error}</div>;
        }
        const rawDays = Array.isArray(detail?.data)
            ? detail.data
            : detail?.data?.forecastDates || [];

        if (rawDays.length === 0) {
            return <div style={{padding: 24, color: "#666"}}>Bu dönem için detay bulunamadı.</div>;
        }

        return (
            <DaysContainer>
                {rawDays.map((day, dayIndex) => {
                    const dayKey = `${subCompanyId}-${dayIndex}`;
                    const isExpanded = expandedDays[dayKey];
                    const dateStr = day.forecastDate || day.date;
                    const formattedDate = dateStr ? moment(dateStr).format("DD.MM.YYYY") : "-";
                    const dailyTotal =
                        day.totalClientAmount ??
                        (day.transactions || []).reduce(
                            (sum, t) => sum + (t.courierAmount || 0),
                            0
                        );

                    return (
                        <DayWrapper key={dayIndex}>
                            <DayRow onClick={() => toggleDay(subCompanyId, dayIndex)}>
                                <DayIcon>
                                    <CalendarOutlined />
                                </DayIcon>
                                <DayInfo>{formattedDate}</DayInfo>
                                <DayTotal>
                                    <span className="label">Günlük Toplam Harcama</span>
                                    <span className="amount">{formatCurrency(dailyTotal)}</span>
                                    <span className="arrow">
                                        {isExpanded ? <UpOutlined /> : <DownOutlined />}
                                    </span>
                                </DayTotal>
                            </DayRow>
                            {isExpanded && (
                                <ExpandedDayContent>
                                    <h4>Alt Firma Toplam Bilgisi</h4>
                                    {(day.transactions?.length > 0 && (
                                        <DefaultTransactionTable
                                            transactions={day.transactions}
                                            subTypeLabels={DETAIL_TABLE_SUB_TYPE_LABELS}
                                            Wrapper={DayTransactionTableWrapper}
                                        />
                                    )) ||
                                        null}
                                    {day.couriers?.length > 0 ? <h4>Kuryeler</h4> : null}
                                    {day.couriers?.map(courier => {
                                        const courierKey = `${subCompanyId}-${dayIndex}-${courier.courierId}`;
                                        return (
                                            <CourierSection
                                                key={courier.courierId}
                                                courier={courier}
                                                isExpanded={!!expandedCouriers[courierKey]}
                                                onToggle={() =>
                                                    toggleCourier(
                                                        subCompanyId,
                                                        dayIndex,
                                                        courier.courierId
                                                    )
                                                }
                                                subCompanyNames={subCompanyNames}
                                            />
                                        );
                                    })}
                                </ExpandedDayContent>
                            )}
                        </DayWrapper>
                    );
                })}
            </DaysContainer>
        );
    };
    const {rows: rawRows, dataCount} = normalizeTableData(data);
    const filteredData = (rawRows || []).filter(row => row?.subcompanyId && row?.subcompanyName);

    return (
        <SubcompanyTableWrapper>
            <Table
                dataSource={filteredData}
                loading={loading}
                rowKey={row => row?.subcompanyId}
                pagination={{
                    total: dataCount,
                    current: pageParam,
                    pageSize: 10,
                    hideOnSinglePage: true,
                    showSizeChanger: false,
                    onChange: page => setPageParam(page)
                }}
                expandable={{
                    expandedRowRender: renderExpandedSubCompany,
                    expandedRowKeys: expandedSubCompanies,
                    onExpand: handleSubCompanyExpand
                }}
            >
                <Table.Column
                    title="Alt Firma"
                    render={(_, row) => {
                        const name = row?.subcompanyName;
                        return (
                            <span style={{fontWeight: 500}}>
                                {name}
                                {loadingSubCompanyIds?.includes?.(row?.subcompanyId) && (
                                    <Spin size="small" style={{marginLeft: 8}} />
                                )}
                            </span>
                        );
                    }}
                />
                <Table.Column
                    title="Toplam"
                    render={(_, row) => (
                        <span style={{fontWeight: 500}}>
                            {formatCurrency(row.totalClientAmount ?? row.totalCourierAmount)}
                        </span>
                    )}
                />
            </Table>
        </SubcompanyTableWrapper>
    );
};
